import { ObjectPKInput, ObjectSKInput } from "../aws/DynamoDbApiTypes";
import { ShippingNameAndAddress } from "../lulu/LuluPrintTypes";
import { DynamoKey } from "./BaseDynamoClass";
import { UnderscoreKeys } from "../util/Types";
import { BasicError } from "./BasicError";
import { UserOwnedClass, UserOwnedFields } from "./UserOwnedClass";

export interface UserPKInput extends ObjectPKInput {
	userId: string;
}

export type UserFields = UserOwnedFields & {
	emailAddress: string;
	firstName: string | undefined;
	lastName: string | undefined;
	termsAgreed: boolean;
	termsVersion: string | undefined;
	termsDate: number | undefined;
	marketingAgreed: boolean;
	marketingDate: number | undefined;
	shippingAddresses: ShippingNameAndAddress[];
	defaultShippingAddressIndex: number;
	isAdmin: boolean | undefined;
	emailBounce: boolean | undefined;
	emailBounceDate: number | undefined;
	emailComplaint: boolean | undefined;
	emailComplaintDate: number | undefined;
	transactionalAgreed: boolean;
};

export class User<TFields extends UserFields = UserFields> extends UserOwnedClass<TFields> {
	private _emailAddress!: string;
	private _firstName?: string;
	private _lastName?: string;
	private _termsAgreed!: boolean;
	private _termsVersion?: string;
	private _termsDate?: number;
	private _marketingAgreed!: boolean;
	private _marketingDate?: number;
	private _shippingAddresses!: ShippingNameAndAddress[];
	private _defaultShippingAddressIndex!: number;
	private _isAdmin?: boolean;
	override _className: string = "User";
	override _deserializeClassName: string = "User";
	private _transactionalAgreed!: boolean;
	private _emailBounce: boolean | undefined;
	private _emailBounceDate: number | undefined;
	private _emailComplaint: boolean | undefined;
	private _emailComplaintDate: number | undefined;

	override getFieldList(): (keyof UnderscoreKeys<TFields>)[] {
		return [
			...super.getFieldList(),
			"_emailAddress",
			"_firstName",
			"_lastName",
			"_termsAgreed",
			"_termsVersion",
			"_termsDate",
			"_marketingAgreed",
			"_marketingDate",
			"_shippingAddresses",
			"_defaultShippingAddressIndex",
			"_isAdmin",
			"_emailBounce",
			"_emailBounceDate",
			"_emailComplaint",
			"_emailComplaintDate",
			"_transactionalAgreed"
		];
	}

	constructor(props: Partial<TFields>) {
		super(props);
	}
	getPKPrefix(): string {
		return User.getPKPrefix();
	}
	static getPKPrefix(): string {
		return "USER#";
	}
	getParentKey(): DynamoKey | undefined {
		return undefined;
	}
	getParentKeyInput(): DynamoKey | undefined {
		return undefined;
	}
	getParentTableName(): string | undefined {
		//has no parent / same table
		return undefined;
	}
	generateKey(input: any): DynamoKey {
		if (!input.userId) throw new BasicError("Missing userId", "Missing userId");
		return {
			PK: this.getPKPrefix() + this.userId,
			SK: this.getSKPrefix() + this.userId,
		};
	}
	static generateKey(input: any): DynamoKey {
		if (!input.userId) throw new BasicError("Missing userId", "Missing userId");
		return {
			PK: User.getPKPrefix() + input.userId,
			SK: User.getSKPrefix() + input.userId,
		};
	}
	generatePK(): string {
		throw new BasicError("Not implemented", "Method not implemented.");
	}
		generateGSIPK(index: number, key: ObjectPKInput): string {
			switch (index) {
				case 1:
				default:
					return "EMAIL#" + (!!key && key.emailAddress ? key.emailAddress : "");
			}
		}
		generateGSISK(index: number, key: ObjectSKInput): string {
			switch (index) {
				case 1:
				default:
					return this.getSKPrefix() + (!!key && key.userId ? key.userId : "");
			}
		}
		getGlobalIndices(): object {
			return {
				GSI1PK: this.generateGSIPK(1, this),
				GSI1SK: this.generateGSISK(1, this),
			};
		}
	getColumnsForExport(): string[] {
		throw new BasicError("Not implemented", "Method not implemented.");
	}
	getObjectForExport(): object {
		throw new BasicError("Not implemented", "Method not implemented.");
	}
	get emailAddress(): string {
		return this.checkFieldType<"emailAddress">(this._emailAddress);
	}
	set emailAddress(value: string) {
		this._emailAddress = this.checkFieldTypeAndMark<"emailAddress">("_emailAddress", value);
	}

	get firstName(): string | undefined {
		return this.checkFieldType<"firstName">(this._firstName);
	}
	set firstName(value: string | undefined) {
		this._firstName = this.checkFieldTypeAndMark<"firstName">("_firstName", value);
	}

	get lastName(): string | undefined {
		return this.checkFieldType<"lastName">(this._lastName);
	}
	set lastName(value: string | undefined) {
		this._lastName = this.checkFieldTypeAndMark<"lastName">("_lastName", value);
	}

	get termsAgreed(): boolean {
		return this.checkFieldType<"termsAgreed">(this._termsAgreed);
	}
	set termsAgreed(value: boolean) {
		this._termsAgreed = this.checkFieldTypeAndMark<"termsAgreed">("_termsAgreed", value);
	}

	get termsVersion(): string | undefined {
		return this.checkFieldType<"termsVersion">(this._termsVersion);
	}
	set termsVersion(value: string | undefined) {
		this._termsVersion = this.checkFieldTypeAndMark<"termsVersion">("_termsVersion", value);
	}

	get shippingAddresses(): ShippingNameAndAddress[] {
		return this.checkFieldType<"shippingAddresses">(this._shippingAddresses);
	}
	set shippingAddresses(value: ShippingNameAndAddress[]) {
		this._shippingAddresses = this.checkFieldTypeAndMark<"shippingAddresses">("_shippingAddresses", value);
	}

	get defaultShippingAddressIndex(): number {
		return this.checkFieldType<"defaultShippingAddressIndex">(this._defaultShippingAddressIndex);
	}
	set defaultShippingAddressIndex(value: number) {
		this._defaultShippingAddressIndex = this.checkFieldTypeAndMark<"defaultShippingAddressIndex">("_defaultShippingAddressIndex", value);
	}

	get isAdmin(): boolean | undefined {
		return this.checkFieldType<"isAdmin">(this._isAdmin);
	}
	set isAdmin(value: boolean | undefined) {
		this._isAdmin = this.checkFieldTypeAndMark<"isAdmin">("_isAdmin", value);
	}

	get termsDate(): number | undefined {
		return this.checkFieldType<"termsDate">(this._termsDate);
	}
	set termsDate(value: number | undefined) {
		this._termsDate = this.checkFieldTypeAndMark<"termsDate">("_termsDate", value);
	}

	get marketingAgreed(): boolean {
		return this.checkFieldType<"marketingAgreed">(this._marketingAgreed);
	}
	set marketingAgreed(value: boolean) {
		this._marketingAgreed = this.checkFieldTypeAndMark<"marketingAgreed">("_marketingAgreed", value);
	}

	get marketingDate(): number | undefined {
		return this.checkFieldType<"marketingDate">(this._marketingDate);
	}
	set marketingDate(value: number | undefined) {
		this._marketingDate = this.checkFieldTypeAndMark<"marketingDate">("_marketingDate", value);
	}

	get emailBounce(): boolean | undefined {
		return this.checkFieldType<"emailBounce">(this._emailBounce);
	}
	set emailBounce(value: boolean | undefined) {
		this._emailBounce = this.checkFieldTypeAndMark<"emailBounce">("_emailBounce", value);
	}

	get emailBounceDate(): number | undefined {
		return this.checkFieldType<"emailBounceDate">(this._emailBounceDate);
	}
	set emailBounceDate(value: number | undefined) {
		this._emailBounceDate = this.checkFieldTypeAndMark<"emailBounceDate">("_emailBounceDate", value);
	}

	get emailComplaint(): boolean | undefined {
		return this.checkFieldType<"emailComplaint">(this._emailComplaint);
	}
	set emailComplaint(value: boolean | undefined) {
		this._emailComplaint = this.checkFieldTypeAndMark<"emailComplaint">("_emailComplaint", value);
	}

	get emailComplaintDate(): number | undefined {
		return this.checkFieldType<"emailComplaintDate">(this._emailComplaintDate);
	}
	set emailComplaintDate(value: number | undefined) {
		this._emailComplaintDate = this.checkFieldTypeAndMark<"emailComplaintDate">("_emailComplaintDate", value);
	}

	get transactionalAgreed(): boolean {
		return this.checkFieldType<"transactionalAgreed">(this._transactionalAgreed);
	}
	set transactionalAgreed(value: boolean) {
		this._transactionalAgreed = this.checkFieldTypeAndMark<"transactionalAgreed">("_transactionalAgreed", value);
	}

	getSKPrefix(): string {
		return User.getSKPrefix();
	}
	static getSKPrefix(): string {
		return "USER#";
	}
	getPK(): string {
		if (!this.userId) throw new BasicError("Missing userId", "Missing userId");
		return this.getPKPrefix() + this.userId;
	}
	getSK(): string {
		if (!this.userId) throw new BasicError("Missing userId", "Missing userId");
		return this.getSKPrefix() + this.userId;
	}
	public addShippingAddress(address: ShippingNameAndAddress, setAsDefault: boolean = true): number {
		if (!this.shippingAddresses) this.shippingAddresses = [];
		const arrLength = this.shippingAddresses.push(address);
		const index = arrLength - 1;
		if (setAsDefault || index === 0) this.defaultShippingAddressIndex = index;
		this.markFieldDirty("_shippingAddresses");
		return index;
	}
	//TODO remove this and allow multiple shipping address management
	public setSingleShippingAddress(address: ShippingNameAndAddress): number {
		this.shippingAddresses = [];
		this.shippingAddresses.push(address);
		this.defaultShippingAddressIndex = 0;
		this.markFieldDirty("_shippingAddresses");
		return 0;
	}
	public getDefaultShippingAddress(): ShippingNameAndAddress | undefined {
		this.assertFetched("shippingAddresses");
		this.assertFetched("defaultShippingAddressIndex");
		if (!this._shippingAddresses || this.shippingAddresses.length === 0) return undefined;
		return this.shippingAddresses[this.defaultShippingAddressIndex];
	}
	canSendTransactionalEmail(){
		return !this.emailBounce && !this.emailComplaint && this.transactionalAgreed;
	}
}
